import {useContext, createContext, useState, useEffect, useCallback} from "react";
import { CognitoUserPool, CognitoUser, AuthenticationDetails } from "amazon-cognito-identity-js";
import { config } from "../config/config"

export const userPool = new CognitoUserPool({
    UserPoolId: config.cognito.userPoolId,
    ClientId: config.cognito.clientId,
});

export const refreshSession = () => {
    return new Promise((resolve, reject) => {
        const loginTimestamp = sessionStorage.getItem('login_timestamp');
        const MAX_SESSION_TIME = 12 * 60 * 60 * 1000; // 12 hours

        if (loginTimestamp && (Date.now() - parseInt(loginTimestamp, 10) > MAX_SESSION_TIME)) {
            console.log("Sesión expirada (límite 12 horas)");
            logOutInternal();
            reject(new Error("SESSION_EXPIRED"));
            return;
        }

        const currentUser = userPool.getCurrentUser();
        if (currentUser) {
            currentUser.getSession((err, session) => {
                if (err || !session || !session.isValid()) {
                    logOutInternal();
                    reject(err || new Error("INVALID_SESSION"));
                } else {
                    const idToken = session.getIdToken().getJwtToken();
                    sessionStorage.setItem('token', idToken);
                    resolve(idToken);
                }
            });
        } else {
            reject(new Error("NO_USER"));
        }
    });
};

const logOutInternal = () => {
    const currentUser = userPool.getCurrentUser();
    if (currentUser) {
        currentUser.signOut();
    }
    sessionStorage.removeItem('token');
    sessionStorage.removeItem('login_timestamp');
    sessionStorage.removeItem('user');
    if (window.location.pathname !== '/logout' && window.location.pathname !== '/login') {
        window.location.href = '/login';
    }
};

const AuthContext = createContext({
    isAuthenticated: false,
    isLoading: true,
    setSession: () => {
    },
    logOut: () => {
    },
    getToken: () => {
    },
    setToken: () => {
    },
    sessionInfo: null,
});

export function AuthProvider({children}) {
    const [isAuthenticated, setIsAuthenticated] = useState(false);
    const [isLoading, setIsLoading] = useState(true);
    const [sessionInfo, setSessionInfo] = useState(null);

    async function SignIn(userData) {
        const authDetails = new AuthenticationDetails({
            Username: userData.username,
            Password: userData.password,
        });

        const cognitoUser = new CognitoUser({
            Username: userData.username,
            Pool: userPool,
        });
        return new Promise((resolve, reject) => {
            cognitoUser.authenticateUser(authDetails, {
                onSuccess: (session) => {
                  console.log("SESION LOGED", session)
                    // Access token (JWT) para llamadas a APIs protegidas
                    const accessToken = session.getIdToken().getJwtToken();
                    const id_token = session.getIdToken()
                    const user_name = id_token.payload?.name
                    
                    sessionStorage.setItem('login_timestamp', Date.now().toString());
                    setSession(accessToken, user_name);
                    resolve({ ok: true, token: accessToken });
                },
                onFailure: (err) => {
                    reject(err);
                },
                newPasswordRequired: (userAttributes, requiredAttributes) => {
                    reject({ code: "NewPasswordRequired", userAttributes, requiredAttributes, cognitoUser });
                },
            });
        });
    }

    function setSession(token, user_name) {
        sessionStorage.setItem('token', token);
        setIsAuthenticated(true);
        const sessionInfoData = {
            data: [
              {
                name: user_name? user_name : 'Logged-in',
                last_name: user_name? '' : 'User',
              },
            ],
          };
        setSessionInfo(sessionInfoData);
        //getSessionInformation()
    }

    function logOut() {
        logOutInternal();
        setIsAuthenticated(false);
        setSessionInfo(null);
    }

    function getToken() {
        return sessionStorage.getItem('token');
    }

    function setToken(token) {
        return sessionStorage.setItem('token', token);
    }

    const checkAuth = useCallback(() => {
        refreshSession().then(() => {
            setIsAuthenticated(true);
        }).catch((err) => {
            const token = sessionStorage.getItem('token');
            if (token) {
                setIsAuthenticated(true);
            }
        }).finally(() => {
            setIsLoading(false);
        });
    }, []);

    function getUserName() {
        return sessionStorage.getItem('user');
    }

    useEffect(() => {
        checkAuth();
    }, [checkAuth]);

    useEffect(() => {
        if (sessionInfo?.data?.length > 0) {
            sessionStorage.setItem('user', `${sessionInfo.data[0].name} ${sessionInfo.data[0].last_name}`);
        }
    }, [sessionInfo])

    return (
        <AuthContext.Provider value={{isAuthenticated, isLoading, setSession, SignIn, logOut, getToken, setToken, sessionInfo, getUserName}}>
            {children}
        </AuthContext.Provider>
    )
}

export const useAuth = () => useContext(AuthContext);